import moment from 'moment';
import { OrderMerchant } from '../../types/order-merchant';
import {
  mockHongKongSettings,
  mockMalaysianSettings,
  mockSettings,
  mockSingaporeSettings,
  mockIndonesiaSettings,
  mockPhilipinesSettings,
} from '../../mocks/settings';

const auAddress = {
  phone: '0400000000',
  street: ['4 Bligh St', 'Line 2'],
  suburb: 'Sydney',
  postcode: '2000',
  region: 'New South Wales',
  region_code: 'NSW',
  country: 'Australia',
  country_code: 'AU',
};

const sgAddress = {
  phone: '65100000000',
  street: ['1 Raffles Place', '#44-01A'],
  suburb: 'Singapore',
  postcode: '048616',
  region: 'Singapore',
  region_code: 'SG',
  country: 'Singapore',
  country_code: 'SG',
};

const myAddress = {
  phone: '6010000000',
  street: ['Petronas Twin Tower', 'Lower Ground (Concourse) Level'],
  suburb: 'Kuala Lumpur City Centre',
  postcode: '50088',
  region: 'Kuala Lumpur',
  region_code: 'KL',
  country: 'Malaysia',
  country_code: 'MY',
};

const hkAddress = {
  phone: '8520000000',
  street: ['Shop 12A & 12B', 'Level 1 MTR Hong Kong Station'],
  suburb: 'Central',
  postcode: '0000',
  region: 'Hong Kong',
  region_code: 'HK',
  country_code: 'HK',
  country: 'Hong Kong',
  po_box_or_locker: true,
};

const idAddress = {
  phone: '6281212345678',
  street: ['Gowork Millenium Centennial Center', 'Level 38'],
  suburb: 'Jenderal Sudirman',
  postcode: '12920',
  region: 'Jakarta',
  region_code: 'DKI',
  country_code: 'ID',
  country: 'Indonesia',
};

const phAddress = {
  phone: '63212345678',
  street: ['551 F Jhocson St'],
  suburb: 'Sampaloc',
  postcode: '1008',
  region: 'Metro Manila',
  region_code: 'NCR',
  country_code: 'PH',
  country: 'Philippines',
};

const address = (countryCode: string = 'AU') => {
  switch (countryCode) {
    case 'SG':
      return sgAddress;
    case 'MY':
      return myAddress;
    case 'HK':
      return hkAddress;
    case 'ID':
      return idAddress;
    case 'PH':
      return phAddress;
    default:
      return auAddress;
  }
};

const customerReturnCharge = (countryCode: string = 'AU') => {
  switch (countryCode) {
    case 'SG':
      return {
        customer_return_dropoff_charge: 3,
        customer_return_pickup_charge: 5.12,
      };
    case 'ID':
      return {
        customer_return_dropoff_charge: 36000,
        customer_return_pickup_charge: 60000,
      };
    case 'PH':
      return {
        customer_return_dropoff_charge: 130,
        customer_return_pickup_charge: 210,
      };
    default:
      return undefined;
  }
};

export const mockMerchants = [
  {
    merchant_id: 1001,
    address: {
      api_reference: null,
      firstname: 'Michael',
      middlename: 'Gary',
      lastname: 'Scott',
      company: 'Shippit Pty Ltd',
      phone: '+61283224835',
      street: ['1 George Street', 'Suite 101'],
      suburb: 'Sydney',
      postcode: '2000',
      region: 'New South Wales',
      region_code: 'NSW',
      country: 'Australia',
      country_code: 'AU',
      id: 1,
    },
  },
  {
    merchant_id: 1002,
    items: [
      {
        sku: '24-MB02',
        qty: 1,
      },
      {
        sku: '24-UB02',
        qty: 1,
      },
    ],
    address: {
      api_reference: null,
      firstname: 'Michael',
      middlename: 'Gary',
      lastname: 'Scott',
      company: 'Shippit Pty Ltd',
      phone: '+61283224835',
      street: ['1 Flinders Street', 'Suite 101'],
      suburb: 'Melbourne',
      postcode: '3000',
      region: 'Victoria',
      region_code: 'VIC',
      country: 'Australia',
      country_code: 'AU',
      id: 2,
    },
  },
  {
    merchant_id: 1003,
    items: [
      {
        sku: '24-UB02',
        qty: 1,
      },
    ],
    address: {
      api_reference: null,
      firstname: 'Richard',
      middlename: 'Chou',
      lastname: 'Scott',
      company: 'Shippit Pty Ltd',
      phone: '+61400000000',
      street: ['4 Bligh St', 'Suite 101'],
      suburb: 'Melbourne',
      postcode: '3000',
      region: 'Victoria',
      region_code: 'VIC',
      country: 'Australia',
      country_code: 'AU',
      id: 2,
    },
  },
];

const settings = (countryCode: string = 'AU') => {
  switch (countryCode) {
    case 'SG':
      return mockSingaporeSettings;
    case 'MY':
      return mockMalaysianSettings;
    case 'HK':
      return mockHongKongSettings;
    case 'PH':
      return mockPhilipinesSettings;
    case 'ID':
      return mockIndonesiaSettings;
    default:
      return mockSettings;
  }
};

const currencyCode = (countryCode: string = 'AU') => {
  switch (countryCode) {
    case 'SG':
      return 'SGD';
    case 'MY':
      return 'MYR';
    case 'HK':
      return 'HKD';
    case 'PH':
      return 'PHP';
    case 'ID':
      return 'IDR';
    default:
      return 'AUD';
  }
};

type MockReturnsOptions = {
  returnInStore?: boolean;
  selfReturns?: boolean;
};

export const mockMerchant = (
  countryCode: string = 'AU',
  options: MockReturnsOptions = {}
): OrderMerchant => ({
  id: 1,
  slug: 'merchant-slug',
  country_code: countryCode,
  currency_code: currencyCode(countryCode),
  returns_configuration: {
    settings: {
      ...settings(countryCode),
      return_in_store: options.returnInStore,
      store_locator_url: options.returnInStore
        ? 'https://example.com/store-locator'
        : undefined,
      self_returns_enabled: options.selfReturns,
      self_returns_url: options.returnInStore
        ? 'https://example.com/self-return-form'
        : undefined,
    },
    reasons: [
      { id: 1, description: 'Wrong size' },
      { id: 2, description: 'Damaged or Faulty' },
    ],
    conditions: [
      { id: 1, description: 'Worn' },
      { id: 2, description: 'Unworn' },
    ],
    rules: [
      {
        max_age_days: 100000000,
        authorised: true,
        paid_by_merchant: true,
        condition_id: 1,
        reason_id: 1,
      },
      {
        max_age_days: 1000000000,
        authorised: true,
        paid_by_merchant: false,
        condition_id: 2,
        reason_id: 2,
      },
    ],
    refund_methods: [],
  },
});

export const mockReturnRequestInfo = (countryCode: string = 'AU') => ({
  id: 62,
  reference: 'shp000000005',
  api_reference: 5,
  date: moment().format(),
  customer: {
    api_reference: 12,
    email: 'anna.piresh@ewave.com',
    firstname: 'Anna',
    middlename: null,
    lastname: 'Piresh',
    id: 55,
  },

  billing_address: {
    api_reference: 1,
    firstname: 'Anna',
    middlename: null,
    lastname: 'Piresh',
    company: null,
    id: 61,
    ...address(countryCode),
  },

  shipping_address: {
    api_reference: 1,
    firstname: 'Anna',
    middlename: null,
    lastname: 'Piresh',
    company: null,
    id: 61,
    ...address(countryCode),
  },

  items: [
    {
      sku: '24-MB06',
      api_reference: 7,
      name: 'Rival Field Messenger',
      weight: null,
      depth: null,
      length: null,
      width: null,
      options: [
        { id: 'size', label: 'Size', value: 'EUR 46' },
        { id: 'colour', label: 'Colour', value: 'Black / White' },
      ],
      qty: 1,
      qty_ordered: 1,
      qty_invoiced: 1,
      qty_shipped: 4,
      qty_cancelled: 0,
      qty_refunded: 0,
      qty_returned: 0,
      price: 45,
      original_price: 45,
      discount_amount: 0,
      tax: 0,
      is_price_incl_tax: true,
      image_url:
        'http://staging.theathletesfoot.com.au/media/catalog/product/m/b/mb06-gray-0.jpg',
      is_virtual: false,
      id: 127,
    },
    {
      sku: '24-MB02',
      api_reference: 8,
      name: 'Fusion Backpack',
      weight: null,
      depth: null,
      length: null,
      width: null,
      options: [
        { id: 'size', label: 'Size', value: 'EUR 46' },
        { id: 'colour', label: 'Colour', value: 'Black / White' },
      ],
      qty: 1,
      qty_ordered: 1,
      qty_invoiced: 1,
      qty_shipped: 1,
      qty_cancelled: 0,
      qty_refunded: 0,
      qty_returned: 0,
      price: 59,
      original_price: 59,
      discount_amount: 0,
      tax: 0,
      is_price_incl_tax: true,
      image_url:
        'http://staging.theathletesfoot.com.au/media/catalog/product/m/b/mb06-gray-0.jpg',
      is_virtual: false,
      id: 128,
    },
    {
      sku: '24-UB02',
      api_reference: 9,
      name: 'Impulse Duffle',
      weight: null,
      depth: null,
      length: null,
      width: null,
      options: [
        { id: 'size', label: 'Size', value: 'EUR 46' },
        { id: 'colour', label: 'Colour', value: 'Black / White' },
      ],
      qty: 2,
      qty_ordered: 2,
      qty_invoiced: 2,
      qty_shipped: 2,
      qty_cancelled: 0,
      qty_refunded: 0,
      qty_returned: 0,
      price: 74,
      original_price: 74,
      discount_amount: 0,
      tax: 0,
      is_price_incl_tax: true,
      image_url:
        'http://staging.theathletesfoot.com.au/media/catalog/product/m/b/mb06-gray-0.jpg',
      is_virtual: false,
      id: 129,
    },
  ],

  currency_code: currencyCode(countryCode),
  subtotal: 178,
  tax_amount: 0,
  shipping_amount: 15,
  grand_total: 193,
  created_at: '2017-10-10T07:49:55+00:00',
  updated_at: '2017-10-10T07:49:55+00:00',
  ...customerReturnCharge(countryCode),
});

export const mockReturnRequestWithMerchants = {
  ...mockReturnRequestInfo(),
  merchants: mockMerchants,
};

export const mockReturnRequestWithoutMerchants = (
  countryCode: string = 'AU'
) => ({
  ...mockReturnRequestInfo(countryCode),
  merchants: [],
});

export const mockReturnRequestResponse = (
  countryCode: string = 'AU',
  options: MockReturnsOptions = {}
) => ({
  id: 1,
  token:
    'VFe4BSkfemwWJ88R4tOXjsrWIpit0JO4hqcFN0COfJaZeetbk5QKgJUt5z9VS5916XPXj6GpIcLidrFtUUQdDG8PJbUJAZGiKZHuH4VAUkxF3uqFhhjoNhLnuQjUVJ8JXqgNYiStEfMdt7SFD6qkPJ9bIy5nXMcGkaDy3qknk3ORGVoJMIlemR3znY1k82VX24sSMI8qUYlh2Hs1ldOaY2dKwHJe8w3pSm0iIB2I1wWNklhPv0a53vTxTZO4Eveq',
  purchase_reference: 'shp00000005',
  merchant: mockMerchant(countryCode, options),
  shippit_connect_purchase_data: {
    return_request: {
      ...mockReturnRequestWithoutMerchants(countryCode),
    },
  },
  shipping_address_validation: {
    is_valid: true,
    suggested_address: null,
  },
});
